/**
 * Shared session utilities — extracted from HistoryView / AllTasksView.
 */

/**
 * Normalize a media URL from container-internal paths to browser-accessible URLs.
 * Handles /workspace/user-data/ and /workspace/group/ paths from Managed Agent runner containers.
 */
export function normalizeMediaUrl(url) {
  if (typeof url !== 'string') return url;
  // Local-dev api-server proxies upload URLs back with an absolute host
  // header (e.g. `http://localhost:8000/api/media/...`). The playground
  // runs on `127.0.0.1:5173`, which the browser sees as a different
  // origin — `<img src>` cross-origin loads to an HTTP endpoint without
  // CORS just fail silently. Strip the api-server hostname so the path
  // goes through the Vite proxy under the playground's own origin.
  const apiHostMatch = /^https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?(\/api\/.*)$/i.exec(url);
  if (apiHostMatch) {
    return apiHostMatch[1];
  }
  return url;
}

/**
 * Validate that a GCS signed URL is complete (not truncated).
 * Truncated URLs lack the X-Goog-Signature param or end mid-parameter.
 */
function isValidGcsUrl(url) {
  if (!url || typeof url !== 'string') return false;
  if (!url.includes('storage.googleapis.com')) return true; // non-GCS URLs pass
  // Signed URLs must have X-Goog-Signature — if missing, URL is truncated
  return url.includes('X-Goog-Signature=') && !url.endsWith('=');
}

/**
 * Extract the first image src from HTML card data in session results.
 * Used as fallback when session photos are unavailable or broken.
 */
function extractCardImages(session) {
  const result = session.result;
  if (!result || typeof result !== 'object') return [];
  const cards = result.cards;
  if (!cards || typeof cards !== 'object') return [];
  const imgs = [];
  for (const card of Object.values(cards)) {
    const html = card.data?.html || card.data?.htmlContent || '';
    if (!html) continue;
    // Extract img src from HTML (runner file paths or GCS URLs)
    const matches = html.match(/src=["']([^"']+\.(?:jpg|jpeg|png|webp|gif)(?:\?[^"']*)?)['"]/gi);
    if (matches) {
      for (const m of matches) {
        const src = m.match(/src=["']([^"']+)['"]/i)?.[1];
        if (src && !src.startsWith('data:')) imgs.push(src);
      }
    }
  }
  return imgs;
}

/**
 * Extract photo/video URLs from a session's context or prompt.
 * Handles both array and string formats for context.photos,
 * falls back to scanning prompt text for GCS URLs,
 * and finally extracts images from result card HTML.
 * All paths are normalized to browser-accessible URLs.
 */
export function extractSessionPhotos(session) {
  const photos = [];
  if (session.context?.photos) {
    if (Array.isArray(session.context.photos)) {
      for (const item of session.context.photos) {
        if (typeof item === 'string') {
          photos.push({ url: item });
        } else if (item && typeof item === 'object' && item.url) {
          photos.push({ url: item.url, width: item.width, height: item.height });
        }
      }
    } else if (typeof session.context.photos === 'string') {
      const urls = session.context.photos.match(/https?:\/\/[^\s]+/g);
      if (urls) photos.push(...urls.map(url => ({ url })));
    }
  }
  // Fallback 1: scan prompt for GCS URLs (only if complete/valid)
  if (photos.length === 0 && session.prompt) {
    const urls = session.prompt.match(/https:\/\/storage\.googleapis\.com\/[^\s]+/g);
    if (urls) {
      for (const url of urls) {
        if (isValidGcsUrl(url)) photos.push({ url });
      }
    }
  }
  // Fallback 2: extract images from result card HTML (runner-served files)
  if (photos.length === 0) {
    const cardImgs = extractCardImages(session);
    for (const url of cardImgs.slice(0, 3)) {
      photos.push({ url });
    }
  }
  return photos.map(p => ({ ...p, url: normalizeMediaUrl(p.url) }));
}
